import { useEffect } from "react";

export interface EditorHotkeys {
  /** Ctrl+Enter alebo Insert — pridať dielec do vybranej zostavy. */
  onAddDielec?: () => void;
  /** Ctrl+Shift+Z — nová zostava. */
  onAddZostava?: () => void;
  /** Ctrl+D — duplikovať vybranú zostavu. */
  onDuplicate?: () => void;
  /** Delete — zmazať vybraný dielec/zostavu. */
  onDelete?: () => void;
  /** Šípky hore/dole — posun výberu dielca. */
  onMoveSelection?: (smer: 1 | -1) => void;
  /** Alt+↑ / Alt+↓ — posunúť vybraný riadok v zostave (KOLO 176), na
   *  vybranej zostave celú zostavu (KOLO 183). */
  onPosunRiadok?: (smer: 1 | -1) => void;
  /** ? — prehľad skratiek. */
  onHelp?: () => void;
  /** Escape — zruší hromadný výber dielcov. */
  onZrusVyber?: () => void;
  /** Ctrl/Cmd+Z — vráť poslednú zmenu. */
  onUndo?: () => void;
  /** Ctrl/Cmd+C / X / V — schránka riadkov (KOLO 181). */
  onKopirovat?: () => void;
  onVystrihnut?: () => void;
  onVlozit?: () => void;
  enabled?: boolean;
}

function jeVstupnyPrvok(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return (
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT" ||
    target.isContentEditable
  );
}

function jeOtvorenyDialog(): boolean {
  return document.querySelector('[role="dialog"]') !== null;
}

/** Označený text na stránke — Ctrl+C ho má skopírovať, nie riadky. */
function jeOznacenyText(): boolean {
  const vyber = window.getSelection?.();
  return vyber != null && vyber.rangeCount > 0 && !vyber.isCollapsed;
}

/**
 * Klávesové skratky editora zákazky. Ignorujú sa počas písania do
 * inputov a pri otvorenom dialógu — skratky nesmú kradnúť klávesy
 * formulárom (InlineCell má vlastné Enter/Escape/Tab správanie).
 */
export function useEditorHotkeys(hotkeys: EditorHotkeys): void {
  const {
    onAddDielec,
    onAddZostava,
    onDuplicate,
    onDelete,
    onMoveSelection,
    onPosunRiadok,
    onHelp,
    onZrusVyber,
    onUndo,
    onKopirovat,
    onVystrihnut,
    onVlozit,
    enabled = true,
  } = hotkeys;

  useEffect(() => {
    if (!enabled) return;

    function handler(e: KeyboardEvent): void {
      if (jeVstupnyPrvok(e.target) || jeOtvorenyDialog()) return;

      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === "z") {
        // V bunke sa sem nedostaneme (guard vyššie) — tam patrí nativné
        // textové undo prehliadača, user vracia písmená, nie dielce.
        e.preventDefault();
        onUndo?.();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey) {
        // Schránka riadkov (KOLO 181). Označený text ostáva prehliadaču —
        // user kopíruje číslo z bunky, nie riadok.
        const k = e.key.toLowerCase();
        const akcia =
          k === "c" ? onKopirovat : k === "x" ? onVystrihnut : k === "v" ? onVlozit : undefined;
        if (akcia && !jeOznacenyText()) {
          e.preventDefault();
          akcia();
          return;
        }
      }
      if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === "z") {
        e.preventDefault();
        onAddZostava?.();
        return;
      }
      if ((e.ctrlKey && e.key === "Enter") || e.key === "Insert") {
        e.preventDefault();
        onAddDielec?.();
        return;
      }
      if (e.ctrlKey && !e.shiftKey && e.key.toLowerCase() === "d") {
        e.preventDefault();
        onDuplicate?.();
        return;
      }
      if (
        e.altKey &&
        !e.ctrlKey &&
        !e.metaKey &&
        (e.key === "ArrowUp" || e.key === "ArrowDown")
      ) {
        e.preventDefault();
        onPosunRiadok?.(e.key === "ArrowUp" ? -1 : 1);
        return;
      }
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === "Delete") {
        e.preventDefault();
        onDelete?.();
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        onMoveSelection?.(1);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        onMoveSelection?.(-1);
        return;
      }
      if (e.key === "Escape") {
        // Bez preventDefault — Escape má aj iných konzumentov (dialógy),
        // sem sa dostane len keď žiadny nie je otvorený.
        onZrusVyber?.();
        return;
      }
      if (e.key === "?") {
        e.preventDefault();
        onHelp?.();
      }
    }

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [
    enabled,
    onAddDielec,
    onAddZostava,
    onDuplicate,
    onDelete,
    onMoveSelection,
    onPosunRiadok,
    onHelp,
    onZrusVyber,
    onUndo,
    onKopirovat,
    onVystrihnut,
    onVlozit,
  ]);
}
